import * as React from 'react';

import classnames from 'classnames';

import {Checkbox} from 'shells/checkbox/checkbox';
import {Popover} from 'shells/popover/popover';
import {PopoverForm} from 'shells/popover-form/popover-form';
import {Body} from 'shells/typography/body';

import type {GetProspectorContacts_prospectorContacts_mcfxContacts as GetProspectorContacts} from 'nutshell-graphql-types';

import {AUTO_DISTRIBUTE_OPTION} from '../../../core/form/fields/consts';
import {PipelineSelectPicker} from '../../../core/form/fields/pipeline-select-picker';
import {useGetUnlockInfo} from '../../../people-iq/graphql/hooks/use-get-unlock-info';

import {getPopoverTitle, getPopoverSubtitle} from './helpers';

import styles from './add-prospects-popover.module.css';

type Props = {
    selectedContacts: GetProspectorContacts[];
    anchor: HTMLElement | null | undefined;
    onClose: () => void;
    onBlur: () => void;
    onSubmit: (shouldCreateNewLead: boolean, stagesetId?: string | null | undefined) => void;
};

export function AddProspectsPopover(props: Props) {
    const {unlockInfo} = useGetUnlockInfo();

    const popoverTitle = getPopoverTitle(props.selectedContacts);
    const popoverSubtitle = getPopoverSubtitle(unlockInfo, props.selectedContacts.length);

    const isAllowedToAddProspects = Boolean(
        unlockInfo &&
            unlockInfo.unlocksRemaining > 0 &&
            unlockInfo.unlocksRemaining - props.selectedContacts.length >= 0
    );

    const [shouldCreateNewLead, setShouldCreateNewLead] = React.useState(true);
    const [selectedStageset, setSelectedStageset] = React.useState<string>(
        AUTO_DISTRIBUTE_OPTION.value
    );
    const [isSaving, setIsSaving] = React.useState(false);

    const handleShouldCreateNewLeadChange = () => {
        setShouldCreateNewLead((prevShouldCreateNewLead) => !prevShouldCreateNewLead);
    };

    const handleSelectedStagesetChange = (newValue: string) => {
        setSelectedStageset(newValue);
    };

    const handleSubmit = () => {
        setIsSaving(true);
        props.onSubmit(shouldCreateNewLead, shouldCreateNewLead ? selectedStageset : null);
    };

    return (
        <Popover anchor={props.anchor}>
            <PopoverForm
                onClose={props.onClose}
                // @ts-expect-error
                onBlur={props.onBlur}
                onSubmit={handleSubmit}
                title={`${popoverTitle} to Nutshell`}
                allowTitleWrapping={true}
                saveButtonDisabled={!isAllowedToAddProspects}
                saveButtonText={popoverTitle}
                isSaving={isSaving}
                saveButtonSavingText='Adding…'
            >
                <div className={classnames('flex flex-dir-col gap-16', styles['form-container'])}>
                    <Body color='light'>{popoverSubtitle}</Body>
                    <div className='flex flex-dir-col gap-12'>
                        <Checkbox
                            label='Also create new lead'
                            checked={shouldCreateNewLead}
                            onChange={handleShouldCreateNewLeadChange}
                        />
                        {shouldCreateNewLead && (
                            <PipelineSelectPicker
                                selectedPipeline={selectedStageset}
                                onSelectPipeline={handleSelectedStagesetChange}
                                menuPlacement='auto'
                                useAutoDistribute={true}
                            />
                        )}
                    </div>
                </div>
            </PopoverForm>
        </Popover>
    );
}
